import React, { useState } from "react";
import { Bars, TailSpin } from "react-loader-spinner";
import { IoIosArrowDown, IoMdAdd } from "react-icons/io";
import { AiOutlineCloseCircle, AiOutlineExpandAlt } from "react-icons/ai";
import type { AxiosError } from "axios";

import { noTransaction } from "../../../assets/images";
import type { GiftCardCatalog } from "../../../lib/api/giftCardCatalog";
import { useInvalidateGiftCardCatalog } from "../../../lib/api/useGiftCardCatalogQueries";
import {
  useApiDeleteMutation,
  useApiPatchMutation,
} from "../../../lib/api/useApiMutation";

function axiosMessage(err: unknown): string {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as AxiosError<{ message?: string }>).response?.data;
    if (data && typeof data === "object" && "message" in data) {
      return String(data.message ?? "Something went wrong");
    }
  }
  return "Something went wrong";
}

interface GiftcardRateFlowProps {
  setCardModal: React.Dispatch<React.SetStateAction<boolean>>;
  cardId: string;
  allGiftcards: GiftCardCatalog[];
  cardDetails: GiftCardCatalog | null;
  detailLoading: boolean;
  detailError: boolean;
  onRetryDetail: () => void;
  setAddGiftcard: React.Dispatch<React.SetStateAction<boolean>>;
}

const GiftcardRateFlow: React.FC<GiftcardRateFlowProps> = ({
  setCardModal,
  cardId,
  allGiftcards,
  cardDetails,
  detailLoading,
  detailError,
  onRetryDetail,
  setAddGiftcard,
}) => {
  const invalidateCatalog = useInvalidateGiftCardCatalog();
  const [newRate, setNewRate] = useState<{ id: string | null; value: string }>(
    { id: null, value: "" }
  );
  const [openSettings, setOpenSettings] = useState<Record<number, boolean>>({});
  const [bannerError, setBannerError] = useState("");

  const patchGiftCardMutation = useApiPatchMutation<
    unknown,
    { cardId: string; isActive: boolean; thumbnail?: string }
  >((v) => `api/gift-cards/${v.cardId}`, {
    body: (v) => ({ isActive: v.isActive, thumbnail: v.thumbnail }),
    onSuccess: () => invalidateCatalog(),
    onError: (e) => setBannerError(axiosMessage(e)),
  });

  const patchRateMutation = useApiPatchMutation<
    unknown,
    { cardId: string; rateId: string; rate?: string | number; isActive?: boolean }
  >((v) => `api/gift-cards/${v.cardId}/rates/${v.rateId}`, {
    body: (v) => {
      const b: { rate?: string | number; isActive?: boolean } = {};
      if (v.rate !== undefined && v.rate !== "") b.rate = v.rate;
      if (v.isActive !== undefined) b.isActive = v.isActive;
      return b;
    },
    onSuccess: () => invalidateCatalog(),
    onError: (e) => setBannerError(axiosMessage(e)),
  });

  const deleteRateMutation = useApiDeleteMutation<unknown, { cardId: string; rateId: string }>(
    (v) => `api/gift-cards/${v.cardId}/rates/${v.rateId}`,
    {
      onSuccess: () => invalidateCatalog(),
      onError: (e) => setBannerError(axiosMessage(e)),
    }
  );

  const toggleModal = (objectId: number) => {
    setOpenSettings((prev) => ({
      ...prev,
      [objectId]: !prev[objectId],
    }));
  };

  const closeModal = (objectId: number) => {
    setOpenSettings((prev) => ({
      ...prev,
      [objectId]: false,
    }));
  };

  const handleRateChange = (id: string, event: React.ChangeEvent<HTMLInputElement>) => {
    setNewRate({ id, value: event.target.value });
  };

  const handleStatusChange = () => {
    if (!cardDetails?._id) return;
    setBannerError("");
    patchGiftCardMutation.mutate({
      cardId: cardDetails._id,
      isActive: !cardDetails.isActive,
      thumbnail: cardDetails.thumbnail,
    });
  };

  const countryDetail = cardDetails?.rates ?? [];
  const statusBusy = patchGiftCardMutation.isPending;

  return (
    <div className="w-full font-manrope overflow-auto scrollbar-thumb-[#60B7F6] scrollbar-track-white  scrollbar-thin">
      {bannerError ? (
        <div
          className="mx-6 mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-[13px] text-red-100 flex justify-between items-start gap-3"
          role="alert"
        >
          <span>{bannerError}</span>
          <button
            type="button"
            onClick={() => setBannerError("")}
            className="shrink-0 text-red-200 underline text-[12px]"
          >
            Dismiss
          </button>
        </div>
      ) : null}
      <div className="w-full  justify-between h-[62px] bg-[#0D0D0D] px-6 flex items-center">
        <h4
          onClick={() => setCardModal((prev) => !prev)}
          className="font-bold text-[20px] cursor-pointer flex justify-start items-center gap-2 font-sora text-white"
        >
          {cardDetails?.name ?? "Select a card"}
          <IoIosArrowDown />
        </h4>
        {statusBusy ? (
          <TailSpin color="#0B75C2" height={30} width={30} />
        ) : (
          <div className="flex justify-center gap-2 lgss:gap-4 mx-4 lgss:mx-0 items-center flex-col lgss:flex-row">
            {allGiftcards.length >= 1 && cardDetails ? (
              <div
                onClick={handleStatusChange}
                className={`flex w-12 cursor-pointer h-6 rounded-full transition-all duration-500 ${
                  cardDetails.isActive ? "bg-secondary_button" : "bg-gray-600"
                }`}
              >
                <span
                  className={`h-6 w-6 rounded-full transition-all duration-500 bg-gray-100 ${
                    cardDetails.isActive ? "ml-6" : ""
                  }`}
                />
              </div>
            ) : null}
          </div>
        )}
      </div>
      <div className="w-full h-[58px] flex justify-between items-center overflow-auto text-[#617889] text-[14px] font-medium bg-[#1C2227] px-8">
        <h4 className="w-[10%]">Name</h4>
        <h4 className="text-left w-[10%]">Receipt</h4>
        <h4 className="w-[10%] text-right">Country</h4>
        <h4 className=" w-[10%] text-right">Trade Range</h4>
        <h4 className="w-[10%]">Current Rate </h4>
        <h4 className="w-[10%]">New Rate </h4>
        <h4 className="w-[10%]">Settings</h4>
      </div>
      <div className="w-full h-[400px] bg-[#0D0D0D] pb-10 overflow-auto scrollbar-thumb-[#AEBCC6] scrollbar-thin">
        {detailError ? (
          <div className="flex flex-col items-center justify-center pt-12 px-6 gap-3">
            <p className="text-[14px] text-[#AEBCC6] text-center">
              Could not load this gift card. Check your connection and try again.
            </p>
            <button
              type="button"
              onClick={onRetryDetail}
              className="rounded-lg bg-secondary_button px-4 py-2 text-[13px] font-semibold text-white"
            >
              Retry
            </button>
          </div>
        ) : detailLoading ? (
          <div className="flex pt-8 justify-center items-center">
            <Bars color="#0B75C2" height={80} width={80} />
          </div>
        ) : (
          <div className="w-full">
            {countryDetail.length >= 1 ? (
              <div className="w-full">
                {countryDetail.map((country, index) => {
                  const updateRate = () => {
                    if (!String(newRate.value ?? "").trim()) {
                      setBannerError("Enter a new rate before updating.");
                      return;
                    }
                    setBannerError("");
                    patchRateMutation.mutate(
                      {
                        cardId,
                        rateId: country._id,
                        rate: newRate.value,
                      },
                      {
                        onSuccess: () => setNewRate({ id: null, value: "" }),
                      }
                    );
                  };

                  const deleteRate = () => {
                    setBannerError("");
                    deleteRateMutation.mutate(
                      { cardId, rateId: country._id },
                      { onSuccess: () => closeModal(index) }
                    );
                  };

                  const updateToggleState = () => {
                    setBannerError("");
                    patchRateMutation.mutate(
                      {
                        cardId,
                        rateId: country._id,
                        isActive: !country.isActive,
                      },
                      { onSuccess: () => closeModal(index) }
                    );
                  };

                  const rowPatchPending =
                    patchRateMutation.isPending &&
                    patchRateMutation.variables?.rateId === country._id;
                  const rowDeletePending =
                    deleteRateMutation.isPending &&
                    deleteRateMutation.variables?.rateId === country._id;

                  return (
                    <div
                      key={country._id}
                      className="w-full   h-[76px] flex justify-between items-center text-[#AEBCC6] text-[16px] bg-[#1C2227] mt-4 px-8"
                    >
                      <div className="flex justify-start items-center gap-3">
                        <div className="w-[30px] h-[30px] rounded-lg">
                          <img
                            src={cardDetails?.thumbnail}
                            alt=""
                            className="w-full h-full"
                          />
                        </div>
                        <h4 className="text-[14px]">
                          {cardDetails?.name || "No name"}
                        </h4>
                      </div>
                      <h4 className="text-[14px] font-bold uppercase w-[10%]">
                        {" "}
                        {country.type === "physicalCashReceipt"
                          ? "Cash"
                          : country.type === "physicalDebitCardReceipt"
                            ? "Debit"
                            : country.type === "physicalCreditCardReceipt"
                              ? "Credit"
                              : country.type === "physicalActivationReceipt"
                                ? "Activation"
                                : country.type === "physicalNoReceipt"
                                  ? "No Receipt"
                                  : country.type === "ecode"
                                    ? "Ecode"
                                    : ""}
                      </h4>
                      <h4 className="px-2 h-[28px] w-[100px] rounded-sm  text-[14px] font-medium bg-[#1D1D1D] tracking-wider text-white flex justify-center items-center">
                        {country.country}
                      </h4>
                      <h4 className="w-[10%]">{`${country.range.min}-${country.range.max}`}</h4>
                      <h4 className="">{country.rate}</h4>
                      <div className=" flex items-center">
                        <input
                          type="text"
                          className="w-20 h-8 bg-[#0D0D0D] border border-[#617889] rounded-md px-2 outline-none text-[13px] "
                          value={
                            country._id === newRate.id ? newRate.value : ""
                          }
                          onChange={(e) => handleRateChange(country._id, e)}
                        />
                        <button
                          type="button"
                          onClick={updateRate}
                          className="ml-2 h-8 px-2 bg-[#0B75C2] rounded-md text-white"
                        >
                          {rowPatchPending &&
                          patchRateMutation.variables?.rate !== undefined ? (
                            <TailSpin color="#fff" height={15} width={15} />
                          ) : (
                            "Update"
                          )}
                        </button>
                      </div>
                      <div className="ml-4  flex items-center">
                        <button
                          type="button"
                          onClick={() => toggleModal(index)}
                          className="h-8 w-8 bg-[#0B75C2] rounded-md text-white flex justify-center items-center"
                        >
                          <AiOutlineExpandAlt />
                        </button>
                        {openSettings[index] ? (
                          <div className=" fixed inset-0 flex font-manrope justify-center items-center bg-black bg-opacity-50  backdrop-blur-sm z-[60]">
                            <div className="w-[190px] rounded-xl h-[160px] bg-[#1C2227] ">
                              <div className="w-full px-6 text-[16px] font-bold rounded-t-xl h-[50px] flex justify-between items-center bg-[#273037]">
                                Edit
                                <AiOutlineCloseCircle
                                  onClick={() => closeModal(index)}
                                  className="text-[20px] text-secondary_button cursor-pointer"
                                />
                              </div>
                              <div className="mt-4 w-full flex justify-between px-6 items-center">
                                <h4 className="text-white text-[16px] font-bold">
                                  Turn {country.isActive ? "Off" : "On"}
                                </h4>
                                {rowPatchPending &&
                                patchRateMutation.variables?.isActive !==
                                  undefined ? (
                                  <TailSpin
                                    color="#0B75C2"
                                    height={30}
                                    width={30}
                                  />
                                ) : (
                                  <div
                                    onClick={updateToggleState}
                                    className={`flex w-8  cursor-pointer h-4   rounded-full transition-all duration-500 ${
                                      country.isActive
                                        ? "bg-secondary_button"
                                        : "bg-gray-600"
                                    }`}
                                  >
                                    <span
                                      className={`h-4 w-4  rounded-full transition-all duration-500 bg-gray-100 ${
                                        country.isActive ? "ml-4" : ""
                                      }`}
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="mt-4 w-full flex justify-between px-6 items-center">
                                {rowDeletePending ? (
                                  <TailSpin
                                    color="#FFFFFF"
                                    height={25}
                                    width={25}
                                  />
                                ) : (
                                  <button
                                    type="button"
                                    onClick={deleteRate}
                                    className="text-[#DF4E4E] cursor-pointer  text-[16px] font-bold"
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="w-full flex justify-center mt-10 flex-col items-center">
                <img
                  src={noTransaction}
                  alt="no_transaction"
                  className="h-[60px] w-[60px] object-contain"
                />
                <h4 className="text-[14px] font-semibold text-[#617889] mt-4">
                  No Giftcard Rates Yet
                </h4>
              </div>
            )}
          </div>
        )}
      </div>
      <div
        onClick={() => setAddGiftcard((prev) => !prev)}
        className="w-full h-[60px] flex items-center cursor-pointer justify-end bg-[#1C2227] text-[16px] text-white px-6 gap-2"
      >
        <h4 className="flex  gap-2 items-center">
          Add New Rate
          <IoMdAdd />
        </h4>
      </div>
    </div>
  );
};

export default GiftcardRateFlow;
